/* eslint-disable indent */
import { memo, useCallback, useEffect, useMemo } from "react";
import { Clock, Tag as TagIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import type { MDXFrontMatter } from "types";
import { Prose } from "components/atoms/Prose";
import { Tag } from "components/atoms/Tag";
import { formatDate } from "utils/formatDate";
import { slugify } from "utils/utils";
interface BloglistProps {
  blogs: Array<MDXFrontMatter>;
}

// Cached blur data URL for images
const blurDataURL = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADY=";

// Pre-calculate reading time based on word count
const calculateReadingTime = (content: string): number => {
  const words = content.trim().split(/\s+/).length;
  const readingTime = Math.ceil(words / 200); // Average reading speed
  return Math.max(1, Math.min(readingTime, 30)); // Between 1 and 30 mins
};

// Memoized blog item component to reduce re-renders
const BlogItem = memo(({
  blog,
  index
}: {
  blog: MDXFrontMatter;
  index: number;
}) => {
  const readingTime = useMemo(() => {
    return blog.content ? calculateReadingTime(blog.content) : null;
  }, [blog.content]);
  return <article data-slug={blog.slug} className="flex flex-col h-full bg-slate-800/30 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow group">
                {blog.image && <div className="h-48 relative">
                        <Link href={`/blogs/${blog.slug}`} className="block w-full h-full" aria-label={`Read article: ${blog.title}`} title={`Read article: ${blog.title}`}>
                            <Image src={blog.image} alt={blog.title} fill loading="lazy" fetchPriority={index < 3 ? "high" : "auto"} quality={75} placeholder="blur" blurDataURL={blurDataURL} className="object-cover group-hover:scale-105 transition-transform duration-300" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
                        </Link>
                    </div>}

                <div className="flex flex-col p-5 flex-grow">
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-3">
                        <div className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            <time suppressHydrationWarning>
                                {formatDate(blog.date)}
                            </time>
                        </div>

                        {readingTime && <span className="inline-flex items-center text-sky-400/80">
                                <Clock className="h-3 w-3 mr-1" />
                                {readingTime} min
                            </span>}
                    </div>

                    <h3 className="text-lg font-bold mb-2">
                        <Link href={`/blogs/${blog.slug}`} className="text-white hover:text-sky-400 transition-colors" aria-label={`Read article: ${blog.title}`} title={`Read article: ${blog.title}`}>
                            {blog.title}
                        </Link>
                    </h3>

                    {blog.description && <div className="mb-4 flex-grow">
                            <p className="text-slate-300 text-sm line-clamp-3">
                                {blog.description}
                            </p>
                        </div>}

                    {blog.tags && blog.tags.length > 0 && <div className="mt-auto pt-3 flex flex-wrap gap-2">
                            {blog.tags.slice(0, 3).map((tag, idx) => <Tag key={idx} href={`/blogs/tagged/${slugify(tag)}`}>
                                    {tag}
                                </Tag>)}
                            {blog.tags.length > 3 && <span className="text-xs text-slate-400">
                                    +{blog.tags.length - 3} more
                                </span>}
                        </div>}
                </div>
            </article>;
});
BlogItem.displayName = "BlogItem";

// Featured blog component
const FeaturedBlog = memo(({
  blog
}: {
  blog: MDXFrontMatter;
}) => {
  const readingTime = useMemo(() => {
    return blog.content ? calculateReadingTime(blog.content) : null;
  }, [blog.content]);
  return <div className="mb-16 border-b border-slate-700 pb-12">
            <article>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    {blog.image && <div className="lg:col-span-7 h-72 sm:h-96 relative rounded-xl overflow-hidden shadow-xl">
                            <Link href={`/blogs/${blog.slug}`} className="block w-full h-full" aria-label={`Read featured article: ${blog.title}`} title={`Read featured article: ${blog.title}`}>
                                <Image src={blog.image} alt={blog.title} fill priority fetchPriority="high" quality={85} placeholder="blur" blurDataURL={blurDataURL} className="object-cover hover:scale-105 transition-all duration-500 ease-in-out" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 800px" />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                            </Link>
                        </div>}

                    <div className={`${blog.image ? "lg:col-span-5" : "lg:col-span-12"} flex flex-col space-y-4`}>
                        <div className="flex items-center text-sm text-slate-400 space-x-4">
                            <div className="flex items-center">
                                <Clock className="h-4 w-4 mr-1" />
                                <time suppressHydrationWarning>
                                    {formatDate(blog.date)}
                                </time>
                            </div>

                            {readingTime && <div className="flex items-center">
                                    <span className="inline-flex items-center">
                                        <Clock className="h-4 w-4 mr-1 text-sky-400" />
                                        {readingTime} min read
                                    </span>
                                </div>}

                            {blog.tags && blog.tags.length > 0 && <div className="flex items-center">
                                    <TagIcon className="h-4 w-4 mr-1" />
                                    <span>{blog.tags[0]}</span>
                                </div>}
                        </div>

                        <h2 className="text-3xl md:text-4xl font-bold">
                            <Link href={`/blogs/${blog.slug}`} className="text-white hover:text-sky-400 transition-colors" aria-label={`Read featured article: ${blog.title}`} title={`Read featured article: ${blog.title}`}>
                                {blog.title}
                            </Link>
                        </h2>

                        {blog.description && <div>
                                <Prose>
                                    <p className="text-slate-300 text-lg">
                                        {blog.description}
                                    </p>
                                </Prose>
                            </div>}

                        <div>
                            <Link href={`/blogs/${blog.slug}`} className="inline-flex items-center text-sky-400 hover:text-sky-300 font-medium" aria-label={`Read full article: ${blog.title}`} title={`Read full article: ${blog.title}`}>
                                Read more
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </article>
        </div>;
});
FeaturedBlog.displayName = "FeaturedBlog";
export const BlogList: React.FC<BloglistProps> = ({
  blogs
}) => {
  // Create featured and regular blogs
  const featuredBlog = blogs[0];
  const regularBlogs = blogs.slice(1);

  // Optimize IntersectionObserver with useCallback to prevent recreation
  const setupObserver = useCallback(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("data-slug");
          if (id) {
            observer.unobserve(entry.target);
          }
        }
      });
    }, {
      rootMargin: "200px"
    });
    document.querySelectorAll("[data-slug]").forEach(el => {
      observer.observe(el);
    });
    return observer;
  }, []);

  // We use IntersectionObserver in client-side only
  useEffect(() => {
    const observer = setupObserver();
    return () => observer?.disconnect();
  }, [setupObserver]);
  return <div className="max-w-7xl mx-auto" data-sentry-component="BlogList" data-sentry-source-file="index.tsx">
            {/* Featured blog post */}
            {featuredBlog && <FeaturedBlog blog={featuredBlog} />}

            {/* Regular blog posts in a grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                {regularBlogs.map((blog, index) => <BlogItem key={blog.slug} blog={blog} index={index} />)}
            </div>
        </div>;
};